import { tabsApiConnector } from 'apiConnector/tabs/tabs';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeout, withLatestFrom } from 'rxjs/operators';
import { dynamicFormEnrichmentReceivedThunk } from 'state/dynamic/dynamicForm.thunks';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

export function dynamicFormRollbackEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { httpClient, frontLogger }) =>
    action$.pipe(
      ofType('FORM_TAB_ROLLBACKED'),
      filter(action => action.workspace === 'dynamic'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => getWorkspaceType(state, tabId) === 'dynamic'),
      mergeMap(([{ tabId }, _state]) => {
        const api = tabsApiConnector(httpClient, 'dynamic');

        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        return api.getTabContent<DynamicFormResult>(tabId).pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              values,
              blocksMetadata,
              fieldsMetadata,
              productDescription,
              version: newVersion,
              actions,
              quoteQuery,
              errors,
              allowedRequests,
            }) =>
              of(
                dynamicFormEnrichmentReceivedThunk(
                  tabId,
                  blocksMetadata,
                  fieldsMetadata,
                  productDescription,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  allowedRequests,
                  errors,
                ),
              ),
          ),
          timeout({
            each: timeoutInMillis,
            with: () => throwError(() => new Error('timeout')),
            scheduler: appScheduler,
          }),
          catchError(error => {
            frontLogger.error('Unable to rollback session on dynamic {@FrontError}', error);
            return of(commonFormActions.fetchFormEnrichmentFailed('dynamic', tabId, error));
          }),
        );
      }),
    );
}
